import { UseFormReturn } from "react-hook-form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import { Button } from "@themes/pollinator/ui/Button";
import type { PhoneCollectionFormData } from "@core/features/profile";

export type Props = {
  /** Form instance from react-hook-form */
  form: UseFormReturn<PhoneCollectionFormData>;
  /** Whether form is dirty (has changes) */
  isDirty: boolean;
  /** Whether form is valid */
  isValid: boolean;
  /** Whether form is submitting */
  isSubmitting: boolean;
  /** Whether the form can be submitted (calculated by container) */
  canSubmit: boolean;
  /** Form submit handler */
  onSubmit: () => void;
  /** Skip handler (optional) */
  onSkip?: () => void;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
};

/**
 * Presentational component for phone collection form.
 * Collects phone number with confirmation and SMS notification opt-in.
 * Used during onboarding for Google OAuth users.
 */
export function PhoneCollectionForm({
  form,
  isDirty,
  isValid,
  isSubmitting,
  canSubmit,
  onSubmit,
  onSkip,
  primaryButtonText = "Save & Continue",
  showActionButtons = true,
}: Props) {

  return (
    <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-6">
      <FormField
        name="phoneNumber"
        label="Phone Number"
        component="PhoneInput"
        placeholder="Enter your phone number"
      />

      <FormField
        name="confirmPhoneNumber"
        label="Confirm Phone Number"
        component="PhoneInput"
        placeholder="Confirm your phone number"
      />

      <FormField
        name="isTextNotificationsEnabled"
        label="Send me SMS notifications"
        component="Checkbox"
        description="Receive important program updates via text message"
      />

      {showActionButtons && (
        <div className="flex flex-col gap-2 mt-4">
          <ActionButtonsGroup
            primaryButtonText={isSubmitting ? "Saving..." : primaryButtonText}
            onPrimaryClick={onSubmit}
            className="w-full"
            primaryButtonProps={{
              disabled: isSubmitting,
              visuallyDisabled: !isSubmitting && !canSubmit,
              className: "w-full",
            }}
          />
          {onSkip && (
            <Button
              variant="link"
              onClick={onSkip}
              className="w-full"
              data-testid="phone-collection-skip"
            >
              Skip for now
            </Button>
          )}
        </div>
      )}
    </Form>
  );
}

export default PhoneCollectionForm;
